import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { formFields } from "./edit_card";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../A5StaticData/form-conf";
import { useTranslation } from "react-i18next";

const filterA5TimeSeriesSchema = z.object({
  a5_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterA5TimeSeriesFields = filterA5TimeSeriesSchema.keyof().enum;

export type FilterA5TimeSeriesSchema = z.infer<typeof filterA5TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterA5TimeSeriesSchema>[] = [
  {
    name: filterA5TimeSeriesFields.a5_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterA5TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];
export function FilterA5TimeSeries() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.A5_TIME_SERIES;

  const fields: Field[] = [
    {
      name: filterA5TimeSeriesFields.a5_static_datum_name_contains,
      label: t(`${tableName}.searchbox.a5_static_datum_name_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_STATIC_DATA}`,
      id_column: formFields.a5_static_datum_id,
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterA5TimeSeriesSchema>(
        assetDepFields,
        filterA5TimeSeriesFields.a5_static_datum_name_contains,
      ),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterA5TimeSeriesFields.asset_class_1_equals,
      assetSubclassFieldName: filterA5TimeSeriesFields.asset_class_2_equals,
      assetClassLabel: t(`${Tables.A5_TIME_SERIES}.filter.asset_class_1`),
      assetSubclassLabel: t(`${Tables.A5_TIME_SERIES}.filter.asset_class_2`),
      client: Clients.A5,
      staticDataTableName: Tables.A5_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterA5TimeSeriesFields.isin_contains,
      label: t(`${tableName}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A5}/${Tables.A5_STATIC_DATA}`,
      id_column: formFields.a5_static_datum_id,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterA5TimeSeriesSchema>(
        assetDepFields,
        filterA5TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterA5TimeSeriesFields.currency_contains,
      label: t(`${tableName}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterA5TimeSeriesFields.date_from },
      to: { name: filterA5TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterA5TimeSeriesFields.create_from },
      to: { name: filterA5TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterA5TimeSeriesSchema}
      client={Clients.A5}
      tableName={Tables.A5_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
